// ---- Focus / combat camera ----
// Click an NPC (the knight, a cow, the pig…) to "focus" it: the player then
// always faces that target, a glowing ring marks it on the ground, a top-right
// character card (shared `.char-card` shell with the local player card) shows
// a headshot + name, and the camera locks behind the player along the
// player→target line so the two stay framed together. While focused, WASD
// becomes target-relative (W toward, S away, A/D strafe). Click empty space,
// click the target again, or press Esc to release.
//
// PROPS focus too, but lightly: clicking an interactable deco (a chest, a tree,
// a signpost — anything whose spec has an `interact` block) shows the same
// ring/card/action-bar treatment WITHOUT the camera lock or target-relative
// movement (getFocus() stays NPC-only, so game.ts never locks onto a prop).
// The prop picking itself lives in src/interact.ts, injected here via
// registerPropPicker so this module needs no deco knowledge.
//
// The focus *state* lives here; game.ts reads getFocus() each frame to swap the
// movement basis / facing / camera, and calls updateFocus() to drive the ring.

import * as THREE from "three";
import { npcs, type NpcHandle } from "./npc/index.ts";
import { sendEngage, sendDisengage } from "./net.ts";
import { game } from "./state.ts";
import { paintFocusPortrait } from "./focus-portrait.ts";
import { activeStatuses, type StatusDef } from "./status-ui.ts";
import { registerHoverProbe } from "./cursor.ts";
import { UI_ICON } from "./ui-icons.ts";

const COMBAT_COLOR = 0xff5a3c; // warm "combat" orange-red — fightable target
const PEACE_COLOR = 0x3c8aff; // cool blue — non-combative target
const PROP_COLOR = 0xe8b84b;  // warm amber — an interactable prop
const FIRE_COLOR = 0xff7a28;  // campfire orange — focused live fire

// Accent borders on the focus card (mirrors the ground ring colors).
const COMBAT_BORDER = "#ff5a3c";
const PEACE_BORDER = "#3c8aff";
const PROP_BORDER = "#e8b84b";
const FIRE_BORDER = "#ff7a28";

/** A focused interactable prop (built by src/interact.ts's picker). Static —
 *  position is captured at pick time (props never move). */
export type PropHandle = {
  id: string;
  name: string;
  verb: string;
  x: number; y: number; z: number;
  /** Visual footprint (spec radius · instance scale) — sizes the ground ring. */
  radius: number;
};

/** A focused player-started campfire (built by src/campfire.ts's picker). */
export type FireHandle = {
  id: string;
  x: number; y: number; z: number;
  radius: number;
  /** True if this client owns the fire (may extinguish). */
  mine: boolean;
  /** Epoch ms the fire burns out. */
  readyAt: number;
};

let focused: NpcHandle | null = null;
let focusedProp: PropHandle | null = null;
let focusedFire: FireHandle | null = null;
let ring: THREE.Group | null = null;

// Injected by src/interact.ts (initInteract) — raycasts the interactable decos.
type PropPicker = (rc: THREE.Raycaster) => { prop: PropHandle; distance: number } | null;
let propPicker: PropPicker | null = null;
export function registerPropPicker(fn: PropPicker): void {
  propPicker = fn;
}

// Injected by src/campfire.ts — raycasts live campfire meshes.
type FirePicker = (rc: THREE.Raycaster) => { fire: FireHandle; distance: number } | null;
let firePicker: FirePicker | null = null;
export function registerFirePicker(fn: FirePicker): void {
  firePicker = fn;
}

const raycaster = new THREE.Raycaster();
const ndc = new THREE.Vector2();

// The fightable NPC we've told the server we're focused on (its id), or null.
// Kept in lock-step with `focused` for engage/disengage bookkeeping (primary
// attack hint, cleave eligibility). Focus alone does NOT start a fight — the
// server only chases on territory/assist/a landed hit.
let engagedId: string | null = null;

/** The single funnel for changing focus: updates `focused` and, when the focused
 *  *fightable* target changes, tells the server (engage/disengage bookkeeping —
 *  not combat start). All focus mutations (click, Esc, auto-clear, clearFocus)
 *  go through here. Focusing an NPC always drops any prop/fire focus — the kinds
 *  are mutually exclusive. */
function setFocus(next: NpcHandle | null): void {
  focused = next;
  if (next) { focusedProp = null; focusedFire = null; }
  const wantId = next && next.fightable ? next.spec.id : null;
  if (wantId === engagedId) return;
  if (engagedId) sendDisengage(engagedId);
  if (wantId) sendEngage(wantId);
  engagedId = wantId;
}

/** Focus a prop (or null to release). Drops any NPC/fire focus — and with it the
 *  engagement — so the light prop focus never fights the combat camera. */
function setPropFocus(next: PropHandle | null): void {
  if (next) { setFocus(null); focusedFire = null; }
  focusedProp = next;
}

/** Focus a campfire (or null to release). Light focus like props (no camera lock). */
function setFireFocus(next: FireHandle | null): void {
  if (next) { setFocus(null); focusedProp = null; }
  focusedFire = next;
}

/** The currently-focused NPC, or null. Props never show up here — the combat
 *  camera / target-relative movement in game.ts stays NPC-only. */
export function getFocus(): NpcHandle | null {
  return focused;
}

/** The currently-focused interactable prop, or null (see src/interact.ts). */
export function getPropFocus(): PropHandle | null {
  return focusedProp;
}

export function clearPropFocus(): void {
  setPropFocus(null);
}

/** The currently-focused campfire, or null (see src/campfire.ts). */
export function getFireFocus(): FireHandle | null {
  return focusedFire;
}

export function clearFireFocus(): void {
  setFireFocus(null);
}

/** Refresh a live fire's handle (readyAt/mine) while it stays focused — campfire
 *  deltas call this so the timer + Extinguish slot stay honest. */
export function refreshFireFocus(next: FireHandle | null): void {
  if (!focusedFire) return;
  if (!next || next.id !== focusedFire.id) { setFireFocus(null); return; }
  focusedFire = next;
}

/** Distance to the nearest focusable thing (living NPC, prop, or campfire)
 *  under the given raycaster, or Infinity if none. Lets other click owners
 *  (e.g. fishing's cast) yield when a target is in front of what they'd claim —
 *  so clicking Marlow talks to him even with a rod out over water. */
export function nearestFocusableDistance(rc: THREE.Raycaster): number {
  let best = Infinity;
  for (const npc of npcs()) {
    if (!npc.group.visible || npc.defeated) continue;
    const inter = rc.intersectObject(npc.group, true);
    if (inter.length && inter[0].distance < best) best = inter[0].distance;
  }
  const prop = propPicker?.(rc) ?? null;
  if (prop && prop.distance < best) best = prop.distance;
  const fire = firePicker?.(rc) ?? null;
  if (fire && fire.distance < best) best = fire.distance;
  return best;
}

export function clearFocus(): void {
  setFocus(null);
  focusedProp = null;
  focusedFire = null;
}

/** A flat ground ring + inner dot, unlit so it always reads as a UI marker
 *  rather than a shaded prop. Pulsed by updateFocus(). */
function makeRing(): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshBasicMaterial({
    color: COMBAT_COLOR,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const ringMesh = new THREE.Mesh(new THREE.RingGeometry(0.9, 1.15, 40), mat);
  ringMesh.rotation.x = -Math.PI / 2; // lay flat on the ground
  const tick = new THREE.Mesh(new THREE.RingGeometry(0.35, 0.5, 24), mat.clone());
  tick.rotation.x = -Math.PI / 2;
  g.add(ringMesh, tick);
  g.renderOrder = 10; // draw over the terrain to avoid z-fighting flicker
  return g;
}

/**
 * Wire up click-to-focus. Raycasts the pointer against every visible NPC; the
 * nearest hit becomes the focus (clicking the focused one again toggles it off).
 * A click that hits no NPC, or pressing Esc, releases focus.
 */
export function initFocus(
  scene: THREE.Scene,
  camera: THREE.Camera,
  canvas: HTMLCanvasElement,
  banner: HTMLElement,
): void {
  ring = makeRing();
  ring.visible = false;
  scene.add(ring);

  // Pointer cursor over any focusable (NPC / prop / campfire) — canvas cursor
  // is owned by src/cursor.ts; this is the shared probe for all three kinds.
  registerHoverProbe((rc) => nearestFocusableDistance(rc) < Infinity);

  canvas.addEventListener("pointerdown", (e) => {
    if (e.button !== 0) return; // left click only
    if (game.talking) return;   // mid-conversation: clicks don't re-target / release
    if (game.transferring) return; // mid-map-swap: the old world's NPCs are still
    // raycastable through the fade, and a focus set now points at a rig the
    // swap is about to dispose (the visibility self-heal can't release it).
    ndc.x = (e.clientX / window.innerWidth) * 2 - 1;
    ndc.y = -(e.clientY / window.innerHeight) * 2 + 1;
    raycaster.setFromCamera(ndc, camera);

    let hit: NpcHandle | null = null;
    let best = Infinity;
    for (const npc of npcs()) {
      if (!npc.group.visible || npc.defeated) continue;
      const inter = raycaster.intersectObject(npc.group, true);
      if (inter.length && inter[0].distance < best) {
        best = inter[0].distance;
        hit = npc;
      }
    }
    // Interactable props + campfires compete on hit distance — a tree behind
    // the goblin never steals the goblin's click.
    const prop = propPicker?.(raycaster) ?? null;
    const fire = firePicker?.(raycaster) ?? null;
    const propD = prop?.distance ?? Infinity;
    const fireD = fire?.distance ?? Infinity;
    if (prop && propD < best && propD <= fireD) {
      setPropFocus(prop.prop.id === focusedProp?.id ? null : prop.prop);
      return;
    }
    if (fire && fireD < best) {
      setFireFocus(fire.fire.id === focusedFire?.id ? null : fire.fire);
      return;
    }
    // Clicking the focused target again releases it; a new target switches; a
    // miss clears focus (all kinds).
    if (!hit) { setPropFocus(null); setFireFocus(null); }
    setFocus(hit && hit !== focused ? hit : null);
  });

  // Esc releases focus — but while a conversation is open, Esc belongs to talk.ts
  // (it ends the talk and keeps focus). This listener is registered before
  // talk.ts's, so it runs first and no-ops here while talking. (interact.ts's
  // listener comes even earlier and eats Esc while the sign plaque is open.)
  addEventListener("keydown", (e) => {
    if (e.key === "Escape" && (focused || focusedProp || focusedFire) && !game.talking) {
      setFocus(null);
      setPropFocus(null);
      setFireFocus(null);
    }
  });

  // Keep the HUD banner in sync from here so callers don't have to.
  updateBanner(banner);
  // Re-sync the banner each frame via updateFocus(); stash the element.
  bannerEl = banner;
}

let bannerEl: HTMLElement | null = null;

// Cached DOM bits inside #focus (built once on first show).
// Layout mirrors #vitals: .char-card > head (face/icon + name/sub) + optional bars.
let focusMain: HTMLElement | null = null;
let focusPortrait: HTMLCanvasElement | null = null;
let focusIcon: HTMLElement | null = null;
let focusName: HTMLElement | null = null;
let focusSub: HTMLElement | null = null;
let focusBars: HTMLElement | null = null;
let focusHpFill: HTMLElement | null = null;
let focusHpLabel: HTMLElement | null = null;
let focusStaFill: HTMLElement | null = null;
let focusStaLabel: HTMLElement | null = null;
let focusFx: HTMLElement | null = null;
/** Last painted portrait key — skip rebuild when the focused NPC kind is unchanged. */
let lastPortraitKey: string | null = null;
/** Last focus-card identity (name/mode/cc/…) for per-frame diff gate. */
let lastBannerKey = "";
/** Last vitals sig so HP/STA bar DOM only updates when values change. */
let lastVitalsKey = "";

function formatBurnLeft(readyAt: number): string {
  const sec = Math.max(0, Math.ceil((readyAt - Date.now()) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** HP/STA row: oil-paint icon + track (same assets as #vitals — UI_ICON). */
function makeBar(kind: "hp" | "sta"): { row: HTMLElement; fill: HTMLElement; label: HTMLElement } {
  const row = document.createElement("div");
  row.className = "bar-row";
  const icon = document.createElement("img");
  icon.className = "bar-icon";
  icon.src = kind === "hp" ? UI_ICON.health : UI_ICON.stamina;
  icon.alt = "";
  icon.width = 22;
  icon.height = 22;
  icon.draggable = false;
  const bar = document.createElement("div");
  bar.className = "bar";
  const fill = document.createElement("div");
  fill.className = `fill ${kind}`;
  const label = document.createElement("div");
  label.className = "label";
  bar.append(fill, label);
  row.append(icon, bar);
  return { row, fill, label };
}

function ensureFocusDom(banner: HTMLElement): void {
  if (focusMain && focusPortrait && focusIcon && focusName && focusSub && focusBars && focusFx) return;
  banner.replaceChildren();

  // Same .char-card shell as the local player's #vitals card (index.html):
  //   head (portrait | icon + name/sub) + bars (fightable only).
  const main = document.createElement("div");
  main.className = "char-card";

  const head = document.createElement("div");
  head.className = "char-card-head";

  const portrait = document.createElement("canvas");
  portrait.className = "char-card-face";
  portrait.width = 72;
  portrait.height = 72;

  const icon = document.createElement("div");
  icon.className = "char-card-icon";

  const meta = document.createElement("div");
  meta.className = "char-card-meta";
  const name = document.createElement("div");
  name.className = "char-card-name";
  const sub = document.createElement("div");
  sub.className = "char-card-sub";
  meta.append(name, sub);

  head.append(portrait, icon, meta);

  const bars = document.createElement("div");
  bars.className = "char-card-bars";
  const hp = makeBar("hp");
  const sta = makeBar("sta");
  bars.append(hp.row, sta.row);

  main.append(head, bars);

  const fx = document.createElement("div");
  fx.className = "focus-fx";

  banner.append(main, fx);
  focusMain = main;
  focusPortrait = portrait;
  focusIcon = icon;
  focusName = name;
  focusSub = sub;
  focusBars = bars;
  focusHpFill = hp.fill;
  focusHpLabel = hp.label;
  focusStaFill = sta.fill;
  focusStaLabel = sta.label;
  focusFx = fx;
}

/** Show/hide + fill the combat bars (player-card twin). Only fightable live NPCs. */
function setCombatBars(
  show: boolean,
  hp = 0, maxHp = 1,
  sta = 0, maxSta = 1,
): void {
  if (!focusBars || !focusHpFill || !focusHpLabel || !focusStaFill || !focusStaLabel) return;
  if (!show) {
    if (focusBars.classList.contains("show")) focusBars.classList.remove("show");
    lastVitalsKey = "";
    return;
  }
  focusBars.classList.add("show");
  const h = Math.max(0, Math.round(hp));
  const mh = Math.max(1, Math.round(maxHp));
  const s = Math.max(0, Math.round(sta));
  const ms = Math.max(1, Math.round(maxSta));
  const sig = `${h}/${mh}|${s}/${ms}`;
  if (sig === lastVitalsKey) return;
  lastVitalsKey = sig;
  focusHpFill.style.width = `${Math.max(0, (h / mh) * 100)}%`;
  focusHpLabel.textContent = `HP ${h}/${mh}`;
  focusStaFill.style.width = `${Math.max(0, (s / ms) * 100)}%`;
  focusStaLabel.textContent = `STA ${s}/${ms}`;
}

function showPortrait(specKey: string, paint: () => void): void {
  if (!focusPortrait || !focusIcon) return;
  focusPortrait.style.display = "block";
  focusIcon.style.display = "none";
  if (lastPortraitKey !== specKey) {
    lastPortraitKey = specKey;
    paint();
  }
}

function showIcon(glyph: string): void {
  if (!focusPortrait || !focusIcon) return;
  focusPortrait.style.display = "none";
  focusIcon.style.display = "flex";
  focusIcon.textContent = glyph;
  lastPortraitKey = null;
}

function makeFxCard(def: StatusDef): HTMLElement {
  const card = document.createElement("div");
  card.className = `fx-card fx-${def.kind}`;
  card.style.borderColor = def.color;
  card.setAttribute("role", "status");
  card.setAttribute("aria-label", `${def.name}: ${def.desc}`);

  const icon = document.createElement("span");
  icon.className = "fx-icon";
  icon.textContent = def.icon;

  const label = document.createElement("span");
  label.className = "fx-label";
  label.textContent = def.name;

  const tip = document.createElement("div");
  tip.className = "fx-tip";
  const tipTitle = document.createElement("strong");
  tipTitle.textContent = def.name;
  const tipBody = document.createElement("span");
  tipBody.textContent = def.desc;
  tip.append(tipTitle, tipBody);

  card.append(icon, label, tip);
  return card;
}

/** Sync the stacked status chips under the headshot. Empty when none / non-NPC. */
function setFocusEffects(list: StatusDef[]): void {
  if (!focusFx) return;
  if (!list.length) {
    if (focusFx.style.display !== "none") {
      focusFx.style.display = "none";
      focusFx.replaceChildren();
    }
    return;
  }
  // Rebuild only when the kind set changes (order is stable via STATUS_ORDER).
  const sig = list.map((d) => d.kind).join(",");
  if (focusFx.dataset.sig === sig && focusFx.style.display === "flex") return;
  focusFx.dataset.sig = sig;
  focusFx.replaceChildren(...list.map(makeFxCard));
  focusFx.style.display = "flex";
}

function updateBanner(banner: HTMLElement | null): void {
  if (!banner) return;

  if (!focused && !focusedProp && !focusedFire) {
    if (banner.style.display !== "none") banner.style.display = "none";
    lastBannerKey = "";
    lastPortraitKey = null;
    lastVitalsKey = "";
    if (focusFx) { focusFx.dataset.sig = ""; focusFx.replaceChildren(); focusFx.style.display = "none"; }
    return;
  }

  ensureFocusDom(banner);

  let key: string;
  let border: string;
  let name: string;
  let sub: string;
  let effects: StatusDef[] = [];
  let combatBars = false;

  if (focused) {
    const combat = focused.fightable && !focused.defeated;
    const kind = focused.spec.kind ?? focused.spec.model ?? focused.spec.id;
    const sn = focused.snare ? 1 : 0;
    const st = focused.stun ? 1 : 0;
    const sl = focused.slow ? 1 : 0;
    // Mode + CC in the identity key; vitals refresh via setCombatBars every frame.
    key = `npc|${focused.spec.id}|${combat ? "c" : "p"}|${sn}${st}${sl}`;
    border = combat ? COMBAT_BORDER : PEACE_BORDER;
    name = focused.spec.name;
    if (combat) {
      // Bars carry HP/STA (player-card twin); sub stays empty so the layout matches.
      sub = "";
      combatBars = true;
      setCombatBars(true, focused.hp, focused.maxHp, focused.stamina, focused.maxStamina);
    } else if (focused.talkable) {
      sub = "◎ Talk";
    } else {
      sub = "◎ Focused";
    }
    effects = activeStatuses({ snare: focused.snare, stun: focused.stun, slow: focused.slow });
    if (key !== lastBannerKey) {
      showPortrait(kind, () => paintFocusPortrait(focusPortrait!, focused!.spec));
    }
  } else if (focusedProp) {
    key = `prop|${focusedProp.id}|${focusedProp.name}`;
    border = PROP_BORDER;
    name = focusedProp.name;
    sub = `✦ ${focusedProp.verb}`;
    if (key !== lastBannerKey) showIcon("✦");
  } else {
    const f = focusedFire!;
    const own = f.mine ? "Your fire" : "Campfire";
    const left = formatBurnLeft(f.readyAt);
    key = `fire|${f.id}|${left}|${f.mine ? 1 : 0}`;
    border = FIRE_BORDER;
    name = own;
    sub = `🔥 ${left} left`;
    if (key !== lastBannerKey) showIcon("🔥");
  }

  if (!combatBars) setCombatBars(false);

  // Effects can change without the rest of the card key (already folded in for
  // NPCs). Always sync chips so a mid-frame snare/stun/slow edge lands.
  setFocusEffects(effects);

  if (key === lastBannerKey) return;
  lastBannerKey = key;
  focusName!.textContent = name;
  focusSub!.textContent = sub;
  // Hide empty sub so combat cards match the player (name-only when bars show).
  if (focusSub) focusSub.style.display = sub ? "" : "none";
  if (focusMain) focusMain.style.borderColor = border;
  banner.style.display = "flex";
}

/** Per-frame: drop focus if the target got hidden (e.g. cutaway), then position
 *  and pulse the ground ring under the focused target (NPC, prop, or fire). */
export function updateFocus(time: number): void {
  if (focused && !focused.group.visible) setFocus(null);
  // Campfire burn-out: handle gone from the world (campfire.ts also clears).
  if (focusedFire && focusedFire.readyAt <= Date.now()) setFireFocus(null);
  updateBanner(bannerEl);

  if (!ring) return;
  if (!focused && !focusedProp && !focusedFire) {
    ring.visible = false;
    return;
  }
  ring.visible = true;
  const col = focused
    ? (focused.fightable && !focused.defeated ? COMBAT_COLOR : PEACE_COLOR)
    : focusedFire ? FIRE_COLOR
    : PROP_COLOR;
  ring.traverse((o) => {
    const m = (o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
    if (m) m.color.setHex(col);
  });
  // Props/fires size the ring to their footprint — NPCs keep the standard ring.
  let size = 1;
  if (focused) {
    const p = focused.group.position;
    ring.position.set(p.x, focused.feetY + 0.06, p.z);
  } else if (focusedFire) {
    const f = focusedFire;
    ring.position.set(f.x, f.y + 0.06, f.z);
    size = Math.min(2.2, Math.max(0.7, f.radius));
  } else {
    const f = focusedProp!;
    ring.position.set(f.x, f.y + 0.06, f.z);
    size = Math.min(2.2, Math.max(0.7, f.radius));
  }
  const pulse = (1 + Math.sin(time * 5) * 0.06) * size;
  ring.scale.set(pulse, pulse, pulse);
  ring.rotation.y = time * 0.6; // slow spin so the marker reads as "active"
}
